import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';

// ============================================================================
// Types
// ============================================================================

export interface CustomerFormData {
  customerId: string;
  customerName: string;
}

export interface CustomerRecord {
  id: string;
  customerId: string;
  customerName: string;
  entityId: string;
  entityName: string;
  businessUnitId: string;
  businessUnitName: string;
  status: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
}

interface CustomerFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CustomerFormData;
  onFormDataChange: (data: CustomerFormData) => void;
  onSave: () => void;
  onClose: () => void;
}

interface CustomerViewModalProps {
  isOpen: boolean;
  record: CustomerRecord | null;
  onClose: () => void;
}

// ============================================================================
// Form Modal (Create / Edit) — Glassmorphism Design
// ============================================================================

export const CustomerFormModal: React.FC<CustomerFormModalProps> = ({
  isOpen,
  mode,
  formData,
  onFormDataChange,
  onSave,
  onClose,
}) => {
  if (!isOpen) return null;

  const title = mode === 'create' ? 'Add New Customer' : 'Edit Customer';

  const handleChange = (field: keyof CustomerFormData, value: string) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">Customer ID *</label>
            <input
              type="text"
              className="form-input"
              value={formData.customerId}
              onChange={(e) => handleChange('customerId', e.target.value.replace(/\s/g, ''))}
              placeholder="e.g., CUST001"
              maxLength={50}
              readOnly={mode === 'edit'}
              style={{
                border: '1px solid #334155',
                ...(mode === 'edit' ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : {}),
              }}
            />
            {mode === 'edit' && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
                Customer ID cannot be changed
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Customer Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.customerName}
              onChange={(e) => handleChange('customerName', e.target.value)}
              placeholder="e.g., Ramesh Kumar"
              maxLength={200}
              style={{ border: '1px solid #334155' }}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSave}>
            <IconSystem.save size={16} />
            {mode === 'create' ? 'Create' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal — Glassmorphism Design
// ============================================================================

export const CustomerViewModal: React.FC<CustomerViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">Customer Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">Customer ID</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', fontWeight: 600 }}>
                {record.customerId}
              </div>
            </div>
            <div>
              <label className="form-label">Customer Name</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.customerName}
              </div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <div style={{ padding: '0.5rem' }}>
                <span style={getStatusBadgeStyle(record.status)}>
                  {record.status}
                </span>
              </div>
            </div>
            <div>
              <label className="form-label">Entity</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.entityName}
              </div>
            </div>
            <div>
              <label className="form-label">Business Unit</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.businessUnitName}
              </div>
            </div>
            <div>
              <label className="form-label">Created By</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {record.createdByName || record.createdBy}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Created At</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {formatDate(record.createdAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
};
